import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import et from "@wa/messages/account/et.json";
import en from "@wa/messages/account/en.json";
import { api } from "../lib/api";
import { isClientPortalHost } from "../lib/brand";

type Messages = Record<string, unknown>;
const MESSAGES: Record<string, Messages> = { et, en };
const STORAGE_KEY = "wa_account_locale";

/**
 * Runtime locales: a superadmin translates the account catalogue into a new
 * language with AI (the admin centre, Settings -> Languages in the host view,
 * the "Account UI" column). The API stores a flat map (the
 * `i18n.system.account.<locale>` Setting), and this file loads it from the
 * public endpoint and nests it back into a `t()` dictionary. A missing key falls
 * back to the English text rather than Estonian: for a user on an extra language
 * an English fallback is the more readable of the two. The base locales (et/en)
 * ship in the bundle. Module-level cache, one fetch per locale.
 */
const runtimeCache = new Map<string, Messages>();

function isLocaleCode(l: string): boolean {
  return /^[a-z]{2}$/.test(l);
}

/** Flat `{"a.b.c": "text"}` into a nested dictionary (a numeric segment becomes an array). */
export function unflattenMessages(flat: Record<string, string>): Messages {
  const root: Record<string, unknown> = {};
  for (const [path, value] of Object.entries(flat)) {
    if (typeof value !== "string") continue;
    const parts = path.split(".");
    let cur: Record<string, unknown> = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i]!;
      const nextIsIndex = /^\d+$/.test(parts[i + 1]!);
      const existing = cur[key];
      if (!existing || typeof existing !== "object") {
        cur[key] = nextIsIndex ? [] : {};
      }
      cur = cur[key] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]!] = value;
  }
  return root;
}

function getByPath(obj: unknown, path: string): string | undefined {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj) as string | undefined;
}

function interpolate(tpl: string, vars?: Record<string, string | number>): string {
  if (!vars) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

interface I18nValue {
  locale: string;
  setLocale: (l: string) => void;
  /**
   * Switch the language for this visit only, without remembering the choice.
   *
   * For a language the CONTENT asks for rather than the reader: a public sales
   * document is written in one language and its page opens in that one, so a
   * German invoice reads as German whichever browser the link is opened in.
   * That is not the reader telling us their preference, and writing it to
   * storage would silently change the interface of a portal client who happens
   * to have been sent one document in another language.
   *
   * Ignores anything that is not a locale code, and does nothing when the
   * language is already the one in force - so it is safe to call from an effect
   * that runs on every payload.
   */
  applyLocale: (l: string) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

/** The `?lang=` parameter. The public signup funnel (husly.app -> `/alusta` ->
 *  account) passes the site's language along so the account opens in the same
 *  one. Any well-formed locale code is honoured, because the catalogue is
 *  dynamic; anything else falls back to the stored or default locale. */
export function localeFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const l = new URLSearchParams(window.location.search).get("lang");
  return l && isLocaleCode(l) ? l : null;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => {
    if (typeof window === "undefined") return "et";
    return localeFromUrl() ?? window.localStorage.getItem(STORAGE_KEY) ?? "et";
  });
  // A version counter, so `t` is re-created when a runtime catalogue arrives late.
  const [runtimeVersion, setRuntimeVersion] = useState(0);

  const setLocale = useCallback((l: string) => {
    setLocaleState(l);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, l);
  }, []);

  const applyLocale = useCallback((l: string) => {
    if (!isLocaleCode(l)) return;
    setLocaleState((current) => (current === l ? current : l));
  }, []);

  // Persist the locale that arrived in the URL - the initializer only sets state,
  // so without this the choice is lost the moment the funnel redirect strips `?lang`.
  useEffect(() => {
    const l = localeFromUrl();
    if (l) window.localStorage.setItem(STORAGE_KEY, l);
  }, []);

  // A runtime locale is not in the bundle, so fetch the stored system translation
  // from the public endpoint. Best effort: on failure `t()` falls back to English.
  useEffect(() => {
    if (locale in MESSAGES || runtimeCache.has(locale) || !isLocaleCode(locale)) return;
    let alive = true;
    void api
      .get<Record<string, string>>(`/public/system-messages/account/${locale}`)
      .then((flat) => {
        runtimeCache.set(locale, unflattenMessages(flat ?? {}));
        if (alive) setRuntimeVersion((v) => v + 1);
      })
      .catch(() => {
        runtimeCache.set(locale, {});
        if (alive) setRuntimeVersion((v) => v + 1);
      });
    return () => {
      alive = false;
    };
  }, [locale]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const dict = MESSAGES[locale] ?? runtimeCache.get(locale);
      const msg = getByPath(dict, key) ?? getByPath(MESSAGES.en, key) ?? key;
      return interpolate(msg, vars);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, runtimeVersion],
  );

  // The browser tab title follows the interface language. index.html carries the
  // first Estonian value before React loads; this updates it on a locale switch.
  // On the operator's client portal it also has to follow the BRAND - a tab
  // reading "Husly" is the one piece of the shell that stays visible after the
  // client switches windows (see lib/brand.ts).
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = t(isClientPortalHost() ? "app.browserTitlePortal" : "app.browserTitle");
  }, [t]);

  const value = useMemo(
    () => ({ locale, setLocale, applyLocale, t }),
    [locale, setLocale, applyLocale, t],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
